import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Clock,
  DollarSign,
  Tag,
  Loader2,
  CheckCircle2,
  XCircle,
  Link2
} from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { useSyncOperations } from "@/hooks/useSyncOperations";
import { 
  listServicos, 
  createServico, 
  updateServico, 
  deleteServico,
  type ServicoRow 
} from "@/integrations/supabase/servicos";
import { ImportServicos } from "@/components/ImportServicos";
import { DialogVinculosEstoqueServico } from "@/components/DialogVinculosEstoqueServico";
import { formatCurrency } from "@/utils/formatCurrency";
import { getServico } from "@/integrations/supabase/servicos";
import { useAuth } from "@/components/AuthProvider";
import { getOnboardingProgress } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { Lightbulb } from "lucide-react";
import { EvolvementCard } from "@/components/EvolvementCard";

export default function Servicos() {
  const queryClient = useQueryClient();
  const { empresa } = useAuth();
  const { invalidateRelated } = useSync();
  const { syncAfterCreate, syncAfterUpdate, syncAfterDelete } = useSyncOperations();
  const [searchTerm, setSearchTerm] = useState("");

  // Buscar progresso do onboarding
  const { data: onboardingProgress, isLoading: onboardingLoading } = useQuery({
    queryKey: ['onboarding-progress', empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  // Debug logs
  useEffect(() => {
    console.log("🔍 Servicos - Onboarding Progress:", {
      empresa: empresa?.id,
      onboardingProgress,
      shouldShowEvolvementCard: onboardingProgress?.shouldShowEvolvementCard,
      shouldShowChecklist: onboardingProgress?.shouldShowChecklist,
      steps: onboardingProgress?.steps
    });
  }, [onboardingProgress, empresa?.id]);
  const [filtroCategoria, setFiltroCategoria] = useState<string>("todos");
  const [filtroAtivo, setFiltroAtivo] = useState<string>("ativos");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingServico, setEditingServico] = useState<ServicoRow | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [servicoParaVinculos, setServicoParaVinculos] = useState<ServicoRow | null>(null);
  const [dialogVinculosOpen, setDialogVinculosOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    preco_padrao: "",
    tempo_estimado: "",
    categoria: "",
    ativo: true,
    item_lista_servico: "",
    codigo_tributario_municipal: "",
    aliquota_nfse: "",
    natureza_operacao: "1",
    regime_especial_tributacao: "",
    optante_simples_nacional: true,
    incentivador_cultural: false,
    prestador_codigo_municipio: "",
    prestador_inscricao_municipal: "",
    codigo_municipio_prestacao: "",
    discriminacao_servico: ""
  });

  const { data: servicos = [], isLoading } = useQuery({
    queryKey: ["servicos"],
    queryFn: () => listServicos()
  });

  const servicosFiltrados = servicos.filter(servico => {
    const matchSearch = servico.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        servico.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = filtroCategoria === "todos" || servico.categoria === filtroCategoria;
    const matchAtivo = filtroAtivo === "todos" || 
                      (filtroAtivo === "ativos" && servico.ativo) ||
                      (filtroAtivo === "inativos" && !servico.ativo);
    return matchSearch && matchCategoria && matchAtivo;
  });

  const categorias = Array.from(new Set(servicos.map(s => s.categoria).filter(Boolean)));

  const handleOpenDialog = (servico?: ServicoRow) => {
    if (servico) {
      setEditingServico(servico);
      setFormData({
        nome: servico.nome || "",
        descricao: servico.descricao || "",
        preco_padrao: servico.preco_padrao?.toString() || "0",
        tempo_estimado: servico.tempo_estimado?.toString() || "",
        categoria: servico.categoria || "",
        ativo: servico.ativo ?? true,
        item_lista_servico: servico.item_lista_servico || "",
        codigo_tributario_municipal: servico.codigo_tributario_municipal || "",
        aliquota_nfse: servico.aliquota_nfse?.toString() || "",
        natureza_operacao: servico.natureza_operacao || "1",
        regime_especial_tributacao: servico.regime_especial_tributacao || "",
        optante_simples_nacional: servico.optante_simples_nacional ?? true,
        incentivador_cultural: servico.incentivador_cultural ?? false,
        prestador_codigo_municipio: servico.prestador_codigo_municipio || "",
        prestador_inscricao_municipal: servico.prestador_inscricao_municipal || "",
        codigo_municipio_prestacao: servico.codigo_municipio_prestacao || "",
        discriminacao_servico: servico.discriminacao_servico || ""
      });
    } else {
      setEditingServico(null);
      setFormData({
        nome: "",
        descricao: "",
        preco_padrao: "",
        tempo_estimado: "",
        categoria: "",
        ativo: true,
        item_lista_servico: "",
        codigo_tributario_municipal: "",
        aliquota_nfse: "",
        natureza_operacao: "1",
        regime_especial_tributacao: "",
        optante_simples_nacional: true,
        incentivador_cultural: false,
        prestador_codigo_municipio: "",
        prestador_inscricao_municipal: "",
        codigo_municipio_prestacao: "",
        discriminacao_servico: ""
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingServico(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    try {
      if (editingServico?.id) {
        const result = await updateServico(editingServico.id, {
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim() || undefined,
          preco_padrao: formData.preco_padrao ? parseFloat(formData.preco_padrao) : 0,
          tempo_estimado: formData.tempo_estimado ? parseInt(formData.tempo_estimado) : undefined,
          categoria: formData.categoria.trim() || undefined,
          ativo: formData.ativo,
          item_lista_servico: formData.item_lista_servico.trim() || undefined,
          codigo_tributario_municipal: formData.codigo_tributario_municipal.trim() || undefined,
          aliquota_nfse: formData.aliquota_nfse.trim() ? parseFloat(formData.aliquota_nfse) : undefined,
          natureza_operacao: formData.natureza_operacao.trim() || undefined,
          regime_especial_tributacao: formData.regime_especial_tributacao.trim() || undefined,
          optante_simples_nacional: formData.optante_simples_nacional,
          incentivador_cultural: formData.incentivador_cultural,
          prestador_codigo_municipio: formData.prestador_codigo_municipio.trim() || undefined,
          prestador_inscricao_municipal: formData.prestador_inscricao_municipal.trim() || undefined,
          codigo_municipio_prestacao: formData.codigo_municipio_prestacao.trim() || undefined,
          discriminacao_servico: formData.discriminacao_servico.trim() || undefined
        });

        if (result.ok) {
          toast.success("Serviço atualizado com sucesso!");
          syncAfterUpdate('servicos', editingServico.id);
          invalidateRelated('servicos');
          queryClient.invalidateQueries({ queryKey: ["servicos"] });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao atualizar serviço");
        }
      } else {
        const result = await createServico({
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim() || undefined,
          preco_padrao: formData.preco_padrao ? parseFloat(formData.preco_padrao) : 0,
          tempo_estimado: formData.tempo_estimado ? parseInt(formData.tempo_estimado) : undefined,
          categoria: formData.categoria.trim() || undefined,
          ativo: formData.ativo,
          item_lista_servico: formData.item_lista_servico.trim() || undefined,
          codigo_tributario_municipal: formData.codigo_tributario_municipal.trim() || undefined,
          aliquota_nfse: formData.aliquota_nfse.trim() ? parseFloat(formData.aliquota_nfse) : undefined,
          natureza_operacao: formData.natureza_operacao.trim() || undefined,
          regime_especial_tributacao: formData.regime_especial_tributacao.trim() || undefined,
          optante_simples_nacional: formData.optante_simples_nacional,
          incentivador_cultural: formData.incentivador_cultural,
          prestador_codigo_municipio: formData.prestador_codigo_municipio.trim() || undefined,
          prestador_inscricao_municipal: formData.prestador_inscricao_municipal.trim() || undefined,
          codigo_municipio_prestacao: formData.codigo_municipio_prestacao.trim() || undefined,
          discriminacao_servico: formData.discriminacao_servico.trim() || undefined
        });

        if (result.ok) {
          toast.success("Serviço criado com sucesso!");
          syncAfterCreate('servicos', result.data);
          invalidateRelated('servicos');
          queryClient.invalidateQueries({ queryKey: ["servicos"] });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao criar serviço");
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao salvar serviço");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este serviço?")) {
      return;
    }

    try {
      setIsDeleting(id);
      const result = await deleteServico(id);
      if (result.ok) {
        toast.success("Serviço excluído com sucesso!");
        syncAfterDelete('servicos', id);
        invalidateRelated('servicos');
        queryClient.invalidateQueries({ queryKey: ["servicos"] });
      } else {
        toast.error(result.error || "Erro ao excluir serviço");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir serviço");
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Serviços"
        description="Gerencie seus serviços para lançamento rápido"
        icon={<Tag className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        leftActions={
          <ImportServicos
            onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ["servicos"] });
            }}
          />
        }
        rightActions={
          <Button onClick={() => handleOpenDialog()} size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:scale-105 transition-all duration-200 text-white font-medium">
            <Plus className="w-4 h-4 mr-2" />
            Novo Serviço
          </Button>
        }
      />

      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Card de Evolução - Acompanhar o onboarding */}
        <EvolvementCard />

        {/* Filtros */}
        <Card className="border-2 border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-purple-500" />
                <Input
                  placeholder="Buscar serviços..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 border-indigo-300 focus:border-indigo-500"
                />
              </div>
              <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                <SelectTrigger className="w-full md:w-48 border-indigo-300 focus:border-indigo-500">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas as categorias</SelectItem>
                  {categorias.map((cat) => (
                    <SelectItem key={cat} value={cat || ""}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroAtivo} onValueChange={setFiltroAtivo}>
                <SelectTrigger className="w-full md:w-48 border-indigo-300 focus:border-indigo-500">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativos">Apenas ativos</SelectItem>
                  <SelectItem value="inativos">Apenas inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Card Informativo de Onboarding - Etapa 2 (Evolução) */}
        {(onboardingProgress?.shouldShowEvolvementCard || onboardingProgress?.shouldShowChecklist) && onboardingProgress?.steps?.some(s => s.step === ONBOARDING_STEPS.FIRST_SERVICES && !s.completed) && (
          <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Lightbulb className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-emerald-900 mb-1">💡 Etapa 2 de 5: Cadastrar serviços</h3>
                  <p className="text-sm text-emerald-800 mb-3">
                    Configure os serviços que você oferece. Isso facilita a oferta de diferentes tipos de serviços nos seus orçamentos e pedidos. Você pode pular essa etapa se preferir continuar com as próximas!
                  </p>
                  <div className="text-xs text-emerald-700 bg-emerald-100 rounded px-3 py-2 inline-block">
                    ✨ Dica: Crie serviços como bordado, gravação, customização, etc.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de Serviços */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Carregando serviços...</p>
          </div>
        ) : servicosFiltrados.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum serviço encontrado</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Serviço
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {servicosFiltrados.map((servico) => (
              <Card key={servico.id} className="border-2 border-purple-100 hover:border-purple-400 hover:shadow-lg transition-all duration-300 rounded-xl group bg-gradient-to-br from-white via-purple-50/30 to-pink-50/20">
                <CardContent className="p-5">
                  <div className="space-y-3.5">
                    {/* Primeira linha: Status, Nome, Categoria */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="flex-shrink-0 pt-0.5">
                          {servico.ativo ? (
                            <div className="p-1.5 bg-green-100 rounded-full">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </div>
                          ) : (
                            <div className="p-1.5 bg-gray-100 rounded-full">
                              <XCircle className="h-4 w-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate text-base">{servico.nome}</p>
                          {servico.categoria && (
                            <Badge className="mt-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200 text-xs font-semibold">
                              {servico.categoria}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Descrição */}
                    {servico.descricao && (
                      <p className="text-sm text-gray-600 leading-relaxed">{servico.descricao}</p>
                    )}

                    {/* Preço e Tempo */}
                    <div className="flex gap-4 bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-lg p-3 border border-purple-100/50">
                      {servico.preco_padrao > 0 && (
                        <div>
                          <p className="text-xs text-purple-600 font-bold uppercase">Preço</p>
                          <p className="font-bold text-green-700 text-base">
                            {formatCurrency({ value: servico.preco_padrao, currency: 'BRL' })}
                          </p>
                        </div>
                      )}
                      {servico.tempo_estimado && (
                        <div>
                          <p className="text-xs text-purple-600 font-bold uppercase">Tempo</p>
                          <p className="font-bold text-purple-700 text-base">{servico.tempo_estimado} min</p>
                        </div>
                      )}
                    </div>

                    {/* Botões */}
                    <div className="flex gap-2.5 pt-1 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(servico)}
                        className="border-2 border-purple-300 text-purple-600 hover:bg-purple-100 hover:border-purple-400 transition-colors font-semibold"
                      >
                        <Edit className="h-4 w-4 mr-1.5" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          // Buscar serviço completo do Supabase
                          const servicoFull = await getServico(servico.id);
                          if (servicoFull) {
                            setServicoParaVinculos(servicoFull);
                            setDialogVinculosOpen(true);
                          }
                        }}
                        className="bg-purple-500 hover:bg-purple-600 transition-colors text-white font-semibold shadow-sm"
                        title="Vínculos de Estoque"
                      >
                        <Link2 className="h-4 w-4 mr-1.5" />
                        Vínculos
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDelete(servico.id)}
                        disabled={isDeleting === servico.id}
                        className="bg-pink-500 hover:bg-pink-600 transition-colors text-white font-semibold shadow-sm disabled:opacity-50"
                      >
                      {isDeleting === servico.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </>
                      )}
                    </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog de criar/editar */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingServico ? "Editar Serviço" : "Novo Serviço"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Serviço *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Conserto de Zíper"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descreva o serviço..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preco_padrao">Preço Padrão (R$) *</Label>
                  <Input
                    id="preco_padrao"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.preco_padrao}
                    onChange={(e) => setFormData({ ...formData, preco_padrao: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tempo_estimado">Tempo Estimado (minutos)</Label>
                  <Input
                    id="tempo_estimado"
                    type="number"
                    min="0"
                    value={formData.tempo_estimado}
                    onChange={(e) => setFormData({ ...formData, tempo_estimado: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoria</Label>
                  <Input
                    id="categoria"
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    placeholder="Ex: Conserto, Ajuste, Personalização"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ativo">Status</Label>
                  <Select
                    value={formData.ativo ? "ativo" : "inativo"}
                    onValueChange={(value) => setFormData({ ...formData, ativo: value === "ativo" })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-sm text-blue-900">Dados Fiscais NFS-e (opcionais)</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Esses dados serão sugeridos automaticamente ao selecionar este serviço na emissão avulsa de NFS-e.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="item_lista_servico">Item Lista de Serviço</Label>
                    <Input
                      id="item_lista_servico"
                      value={formData.item_lista_servico}
                      onChange={(e) => setFormData({ ...formData, item_lista_servico: e.target.value })}
                      placeholder="Ex: 14.01"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="codigo_tributario_municipal">Código Tributário Municipal</Label>
                    <Input
                      id="codigo_tributario_municipal"
                      value={formData.codigo_tributario_municipal}
                      onChange={(e) => setFormData({ ...formData, codigo_tributario_municipal: e.target.value })}
                      placeholder="Ex: 1401001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prestador_codigo_municipio">Município IBGE do Prestador *</Label>
                    <Input
                      id="prestador_codigo_municipio"
                      value={formData.prestador_codigo_municipio}
                      onChange={(e) => setFormData({ ...formData, prestador_codigo_municipio: e.target.value })}
                      placeholder="Ex: 3106200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prestador_inscricao_municipal">Inscrição Municipal Prestador</Label>
                    <Input
                      id="prestador_inscricao_municipal"
                      value={formData.prestador_inscricao_municipal}
                      onChange={(e) => setFormData({ ...formData, prestador_inscricao_municipal: e.target.value })}
                      placeholder="Ex: 123456"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="codigo_municipio_prestacao">Município IBGE da Prestação</Label>
                    <Input
                      id="codigo_municipio_prestacao"
                      value={formData.codigo_municipio_prestacao}
                      onChange={(e) => setFormData({ ...formData, codigo_municipio_prestacao: e.target.value })}
                      placeholder="Ex: 3106200"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discriminacao_servico">Discriminação do Serviço *</Label>
                    <Input
                      id="discriminacao_servico"
                      value={formData.discriminacao_servico}
                      onChange={(e) => setFormData({ ...formData, discriminacao_servico: e.target.value })}
                      placeholder="Ex: Conserto de vestido social"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aliquota_nfse">Alíquota NFS-e (%)</Label>
                    <Input
                      id="aliquota_nfse"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.aliquota_nfse}
                      onChange={(e) => setFormData({ ...formData, aliquota_nfse: e.target.value })}
                      placeholder="Ex: 2.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="natureza_operacao">Natureza da Operação</Label>
                    <Input
                      id="natureza_operacao"
                      value={formData.natureza_operacao}
                      onChange={(e) => setFormData({ ...formData, natureza_operacao: e.target.value })}
                      placeholder="Ex: 1"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="regime_especial_tributacao">Regime Especial de Tributação</Label>
                    <Input
                      id="regime_especial_tributacao"
                      value={formData.regime_especial_tributacao}
                      onChange={(e) => setFormData({ ...formData, regime_especial_tributacao: e.target.value })}
                      placeholder="Ex: 6"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="optante_simples_nacional">Optante Simples Nacional</Label>
                    <Select
                      value={formData.optante_simples_nacional ? "true" : "false"}
                      onValueChange={(value) => setFormData({ ...formData, optante_simples_nacional: value === "true" })}
                    >
                      <SelectTrigger id="optante_simples_nacional">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Sim</SelectItem>
                        <SelectItem value="false">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="incentivador_cultural">Incentivador Cultural</Label>
                    <Select
                      value={formData.incentivador_cultural ? "true" : "false"}
                      onValueChange={(value) => setFormData({ ...formData, incentivador_cultural: value === "true" })}
                    >
                      <SelectTrigger id="incentivador_cultural">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">Não</SelectItem>
                        <SelectItem value="true">Sim</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingServico ? "Atualizar" : "Criar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog de Vínculos de Estoque */}
        {servicoParaVinculos && (
          <DialogVinculosEstoqueServico
            open={dialogVinculosOpen}
            onOpenChange={setDialogVinculosOpen}
            servico={servicoParaVinculos}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["servicos"] });
            }}
          />
        )}
      </div>
    </div>
  );
}

